import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Drawer
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import type { ModerationAction, ReportDTO } from './models/moderation';
import { capitalizeFirstLetter as cfl, replace } from './utils';
import ReviewContentCard from './ReviewContentCard';
import MemberActionCard from './MemberActionCard';
import { useOktaAuth } from '@okta/okta-react';
import AddTagsCard from './AddTagsCard';
import { findPost, fetchDiscussionGroups, discussionGroups, fetchPosts } from "./api/discussions";
import { ModerationActionType, FilterCheckOutputCategory } from './models/moderation';
import PostList from './components/PostList';


export type ActionAdder = (
  reportID: string,
  action: Omit<ModerationAction, 'createdAt' | 'moderatorId'>
) => Promise<void>;

const save = async (report: any, onAddAction: any, action: ModerationActionType, categories: Array<ModerationActionType>) => {
  await onAddAction(report.id, {
    actionType: action,
    actionDescription: '',
    proposedReportCategories: categories,
  });
}

const saveAsUnSafe = async (report: any, onAddAction: any, categories: any) => {
  await save(report, onAddAction, ModerationActionType.REPORT_RESOLVE, categories);
  await save(report, onAddAction, ModerationActionType.CATEGORIES_UPDATE, categories);
}

const saveAsSafe = async (report: any, onAddAction: any) => {
  await save(report, onAddAction, ModerationActionType.UPDATE_AS_SAFE, []);
}


function PostLogs (props: any) {
  if (props.report.moderationLog.length === 0) return <></>;
  const tableRows = props.report.moderationLog.map((ma: any) => (
    <TableRow key={ma.createdAt.toString()}>
      <TableCell component="th" scope="row">{ma.createdAt.toString()}</TableCell>
      <TableCell>{cfl(ma.actionType ?? "")}</TableCell>
      <TableCell>{ma.moderatorId}</TableCell>
      <TableCell>{(ma.proposedReportCategories ?? []).map(cfl).join(' | ')}</TableCell>
    </TableRow>
  ));
  return (
    <Table aria-label="purchases" sx={{ backgroundColor: "#EAE7E4", borderRadius: '16px' }}>
      <TableHead sx={{ backgroundColor: "#D9D9D9"}}>
        <TableRow>
          <TableCell align="left">Date</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Moderator ID</TableCell>
          <TableCell>Proposed categories</TableCell>
        </TableRow>
      </TableHead>
      <TableBody sx={{ backgroundColor: "white"}}>
        {tableRows}
      </TableBody>
    </Table>
  );
}

function RowCollapse(props: {
  report: ReportDTO;
  onAddAction: ActionAdder;
  open: boolean;
}) {
  const [changed, setChanged] = useState<boolean>(false);
  const [post, setPost] = useState<any>(null);
  const [categories, setCategories] = useState<Array<any>>(props.report.reportCategories);
  const initSafeState = props.report.status === "PENDING"
    ? null
    : (props.report.reportCategories.includes(FilterCheckOutputCategory.SAFE) || props.report.reportCategories.length === 0);
  const [safe, setSafe] = useState<boolean|null>(initSafeState);
  const reportCopy = { ...props.report };
  const greeting: boolean = props.report.reportCategories.includes(FilterCheckOutputCategory.GREETING);
  const tagsCard = greeting ? null : <AddTagsCard report={reportCopy} categories={categories} setCategories={ (cat: any) => { setCategories(cat); setChanged(true); } }/>;
  const memberActionCard = <MemberActionCard report={props.report} onAddAction={props.onAddAction}/>;
  const update = () => safe ? saveAsSafe(props.report, props.onAddAction) : saveAsUnSafe(props.report, props.onAddAction, categories);
  const updateButton = (greeting || !changed) ? null : <Button className="update-button" variant="contained" size="small" onClick={ () => update().then(() => setChanged(false)) }>Update</Button>;
  const selectSafe  = (s: boolean) => {
    setSafe(s);
    setChanged(true);
    if (s) {
      setCategories(['SAFE']);
      return;
    }
    setCategories(categories.filter(c => c !== 'SAFE'));
  }
  const { oktaAuth } = useOktaAuth();
  useEffect(() => {
    async function getPost() {
      const _post = await findPost(oktaAuth, props.report.contentId);
      if (!_post) return setPost(null)
      var parent_post = {}
      var replies = []
      if (_post.parentId) {
        replies = await fetchPosts(oktaAuth, _post.discussionGroupId, _post.parentId);
        parent_post = await findPost(oktaAuth, _post.parentId);
      } else {
        replies = await fetchPosts(oktaAuth, _post.discussionGroupId, props.report.contentId);
        parent_post = _post
      }
      setPost({ ...parent_post, replies, selected: props.report.contentId });
    }
    if (props.open) getPost();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);
  const [open, setOpen] = React.useState(false);
  const anchor = 'right';
  let courseName = "";
  if (post) {
    courseName = post.preseed
      ? 'Preseed'
      : post.courseInfo && post.courseInfo.name;
  }
  const courseInfo = (
    <Grid sx={ { marginLeft: '2rem' } }>
    <Typography sx={{ color: 'black', fontSize: '20px' , fontWeight: '600' }} variant="h5" component="div">
      Course
    </Typography>
    <Typography sx={{ color: 'black', fontSize: '20px' , fontWeight: '100' }} variant="h5" component="div">
      { courseName }
    </Typography>
  </Grid>
  );
  const discussionInfo = (
    <Grid>
      <Typography sx={{ color: 'black', fontSize: '20px' , fontWeight: '600' }} variant="h5" component="div">
        Discussion Group
      </Typography>
      <Typography sx={{ color: 'black', fontSize: '20px' , fontWeight: '100' }} variant="h5" component="div">
        { discussionGroups.find(d => d._id === post?.discussionGroupId)?.topic }
      </Typography>
  </Grid>
  );
  const contextInfo = (
    <Grid container>
        { discussionInfo }
        { courseInfo }
    </Grid>
  );
  const contextButton = (
    <React.Fragment key={anchor}>
      <Drawer
        anchor={anchor}
        open={open}
        onClose={() => setOpen(false)}
      >
        { post
          ? <PostList post={post} course={ post.courseInfo?.name } discussionInfo={discussionInfo} select={ post.id }/>
          : null
        }
      </Drawer>
    </React.Fragment>
  );
  return (
    <Collapse
      in={props.open}
      timeout="auto"
      sx={{ width: '100%', backgroundColor: "#FAF6F4" }}
      unmountOnExit
    >
      <Stack>
        <Grid container spacing={2} sx={ { padding: '2rem 4rem'} }>
          <Grid item xs={12}>
            { contextInfo }
          </Grid>
          <Grid item xs={4}>
            <ReviewContentCard post={ post } open={ () => setOpen(true) } greeting={greeting} safe={safe} setSafe={ selectSafe } report={props.report}/>
          </Grid>
          <Grid item xs={4}>
            { !safe ? tagsCard : null }
          </Grid>
          <Grid item xs={4}>
            { (safe || greeting) ? null : memberActionCard }
          </Grid>
          <Grid item xs={12}>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              >
              {updateButton}
              {contextButton}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <PostLogs report={props.report}/>
          </Grid>
        </Grid>
        {/* <Box sx={{ width: "80%", marginTop: 0,  marginLeft: "1rem", marginRight: "2rem", marginBottom: "2rem", opacity: props.report.moderationLog.length === 0 ? 0 : 1 }}></Box> */}
      </Stack>
    </Collapse>
  );
}

function Row(props: { report: ReportDTO; onAddAction: ActionAdder }) {
  const [open, setOpen] = React.useState(false);
  const { report } = props;

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' }, backgroundColor: "#EAE7E4", border: '2px solid white' }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {
            report.status === "PENDING" ?
            <Chip label={cfl(report.status)} sx={{ fontWeight: 600, backgroundColor: "#D64218", color: "white" }}></Chip>
             :
            <Chip label={cfl(report.status)} variant="outlined" sx={{ fontWeight: 600 }}></Chip>
          }
        </TableCell>
        <TableCell>{report.reporterId === "automatic" ? "Automatic" : "HS Member"}</TableCell>
        <TableCell component="th" scope="row">
          <p> { replace(report.reportCategories, 'POSSIBLY_UNSAFE', 'UNSPECIFIED').join(' | ') } </p>
        </TableCell>
        <TableCell>{report.createdOn.toLocaleString()}</TableCell>
        <TableCell component="th" scope="row">
          {report.id}
        </TableCell>
        <TableCell component="th" scope="row">
          {report.course}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={6}>
          <RowCollapse
            report={report}
            open={open}
            onAddAction={props.onAddAction}
          />
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export function ReportsTable(props: {
  reports: ReportDTO[];
  addAction: ActionAdder;
}) {
  const { oktaAuth } = useOktaAuth();
  useEffect(() => {
    fetchDiscussionGroups(oktaAuth);
  }, [oktaAuth]);
  return (
    <div>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>STATUS</TableCell>
              <TableCell>REPORTER</TableCell>
              <TableCell>ISSUE</TableCell>
              <TableCell>DATE</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>COURSE</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.reports.map((report) => (
              <Row
                report={report}
                key={`report-${report.id}`}
                onAddAction={props.addAction}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
